/**
 * Role display utilities — UI-specific helpers for rendering roles.
 *
 * Used by web-app and ams-app for badge rendering, display labels, and filters.
 * Domain classification (ASSIGNABLE_ROLES, NON_ASSIGNABLE_ROLES, isNonAssignableRole) lives
 * in @insio/shared-types.
 */
import { ASSIGNABLE_ROLES, type AssignableRole, getAssignableRoles } from '@insio/shared-types';

/** Virtual role representing a baseline user with no elevated permissions. */
export const MEMBER_ROLE = 'MEMBER' as const;

/** All roles selectable in UI — MEMBER (baseline) + assignable roles. */
export const SELECTABLE_ROLES = [MEMBER_ROLE, ...ASSIGNABLE_ROLES] as const;

export type SelectableRole = typeof MEMBER_ROLE | AssignableRole;

/** Check whether a role string is a selectable role (can be chosen in UI). */
export function isSelectableRole(role: string): role is SelectableRole {
  return (SELECTABLE_ROLES as readonly string[]).includes(role);
}

/** Convert any role string to a SelectableRole, defaulting to MEMBER for unknown/non-assignable roles. */
export function toSelectableRole(role: string): SelectableRole {
  if (isSelectableRole(role)) {
    return role;
  }
  return MEMBER_ROLE;
}

/**
 * Get the primary display role from a roles array (first assignable role, or MEMBER).
 * Useful for table display and filters where a single label is needed.
 */
export function getDisplayRole(roles: readonly string[]): SelectableRole {
  const first = roles.find((r): r is AssignableRole =>
    (ASSIGNABLE_ROLES as readonly string[]).includes(r)
  );
  return first ?? MEMBER_ROLE;
}

/**
 * Get all display roles from a roles array.
 * Returns all assignable roles, or `[MEMBER]` when no assignable roles are present.
 * Useful for table display where all roles should be shown as badges.
 */
export function getDisplayRoles(roles: readonly string[]): SelectableRole[] {
  const assignable = getAssignableRoles(roles);
  return assignable.length > 0 ? assignable : [MEMBER_ROLE];
}

/** Returns the badge color for a single role. ADMIN gets 'primary', others get 'neutral'. */
export function getSingleRoleBadgeColor(role: string): 'primary' | 'neutral' {
  return role === 'ADMIN' ? 'primary' : 'neutral';
}
